import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from "react";
import type { LetterState } from "@mk/shared";
import { gameEdition } from "../lib/edition.ts";
import { messages } from "../i18n/index.ts";
import {
  getStoredKeyboardActionOrder,
  keyboardRowsForActionOrder,
} from "../lib/keyboardLayout.ts";

interface KeyboardProps {
  states: Readonly<Record<string, LetterState>>;
  onPress: (key: string) => void;
  disabled?: boolean;
  /**
   * Tahmin sunucuya gidiyor. Tuşlar bu sırada da tepkisiz ama `disabled`
   * değil: gecikmeli sönümlenip yalnızca bekleme uzarsa görünür oluyor.
   */
  busy?: boolean;
}

const LABELS: Record<string, string> = {
  ENTER: messages.board.enter,
  BACKSPACE: "⌫",
};

const ARIA: Record<string, string> = {
  ENTER: messages.board.submit,
  BACKSPACE: messages.board.backspace,
};

export function Keyboard({ states, onPress, disabled = false, busy = false }: KeyboardProps) {
  const rows = keyboardRowsForActionOrder(
    gameEdition.keyboardRows,
    getStoredKeyboardActionOrder(),
  );

  /*
   * Tuşlar `click` yerine `pointerdown` ile çalışıyor.
   *
   * `click` ancak parmak kalktığında ve fazla kaymadıysa geliyor; hızlı yazan
   * oyuncunun parmağı biraz kaydığında tıklama hiç doğmuyor ve tuş "çalışmamış"
   * gibi görünüyordu. `pointerdown` parmak değdiği an ateşliyor.
   *
   * `click` yine de dinleniyor ama yalnızca `detail === 0` olduğunda: bu, olayın
   * fareden/dokunuştan değil klavyeden ya da ekran okuyucudan geldiği anlamına
   * gelir. Böylece erişilebilirlik korunuyor, çift tetikleme olmuyor.
   */
  const pressWith = (key: string) => ({
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => {
      // Odak kaymasın, metin seçimi ve çift dokunuş yakınlaştırması başlamasın.
      event.preventDefault();
      onPress(key);
    },
    onClick: (event: ReactMouseEvent<HTMLButtonElement>) => {
      if (event.detail === 0) onPress(key);
    },
  });

  return (
    <div className="keyboard" aria-label={messages.board.keyboard} data-busy={busy ? "true" : undefined}>
      {rows.map((row, index) => (
        <div key={index} className="keyboard-row">
          {row.map((key) => {
            const wide = key === "ENTER" || key === "BACKSPACE";
            return (
              <button
                key={key}
                type="button"
                className="key"
                data-state={states[key]}
                data-wide={wide ? "true" : undefined}
                disabled={disabled}
                aria-label={ARIA[key] ?? key}
                {...pressWith(key)}
              >
                {LABELS[key] ?? key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
